/**
 * AI Agent Store Module
 * Manages AI agent chat state, visibility, and messages
 */

// Load persisted state from localStorage (only if minimized)
const loadPersistedState = () => {
	try {
		const saved = localStorage.getItem('kai-chat-state')
		if (saved) {
			const parsed = JSON.parse(saved)
			// Only restore state if it was minimized
			// This ensures we only persist during minimized sessions
			if (parsed.isMinimized === true) {
				return {
					messages: parsed.messages || [],
					isMinimized: parsed.isMinimized,
					selectedMode: parsed.selectedMode || null
				}
			}
		}
	} catch (error) {
		console.error('Error loading persisted KAi state:', error)
	}
	// Default to empty state (fresh start)
	return { messages: [], isMinimized: false, selectedMode: null }
}

const persistedState = loadPersistedState()

const state = {
	isOpen: false,
	isMinimized: persistedState.isMinimized,
	messages: persistedState.messages,
	selectedMode: persistedState.selectedMode,
	isProcessing: false,
	position: 'right', // 'left', 'right', 'bottom'
	width: 400,
	height: 600,
	// Drawer for pinned messages
	drawerOpen: false,
	drawerMinimized: false,
	pinnedMessage: null,
	drawerPlacement: 'right' // 'left' or 'right'
}

// Save state to localStorage (only when minimized)
const saveState = (state) => {
	try {
		// Only persist if minimized, otherwise clear
		if (state.isMinimized) {
			localStorage.setItem(
				'kai-chat-state',
				JSON.stringify({
					messages: state.messages,
					isMinimized: state.isMinimized,
					selectedMode: state.selectedMode
				})
			)
		} else {
			// If not minimized, don't persist
			localStorage.removeItem('kai-chat-state')
		}
	} catch (error) {
		console.error('Error saving KAi state:', error)
	}
}

// Clear persisted state
const clearPersistedState = () => {
	try {
		localStorage.removeItem('kai-chat-state')
	} catch (error) {
		console.error('Error clearing KAi state:', error)
	}
}

const mutations = {
	SET_OPEN(state, isOpen) {
		state.isOpen = isOpen
	},
	SET_MINIMIZED(state, isMinimized) {
		state.isMinimized = isMinimized
		saveState(state)
	},
	ADD_MESSAGE(state, message) {
		state.messages.push({
			...message,
			id: Date.now() + Math.random(),
			timestamp: new Date()
		})
		saveState(state)
	},
	CLEAR_MESSAGES(state) {
		state.messages = []
		state.selectedMode = null
		saveState(state)
	},
	SET_SELECTED_MODE(state, mode) {
		state.selectedMode = mode
		saveState(state)
	},
	SET_PROCESSING(state, isProcessing) {
		state.isProcessing = isProcessing
	},
	SET_POSITION(state, position) {
		state.position = position
	},
	SET_WIDTH(state, width) {
		state.width = width
	},
	SET_HEIGHT(state, height) {
		state.height = height
	},
	UPDATE_LAST_MESSAGE(state, updates) {
		if (state.messages.length > 0) {
			const lastMessage = state.messages[state.messages.length - 1]
			Object.assign(lastMessage, updates)
		}
		saveState(state)
	},
	SET_DRAWER_OPEN(state, isOpen) {
		state.drawerOpen = isOpen
	},
	SET_DRAWER_MINIMIZED(state, isMinimized) {
		state.drawerMinimized = isMinimized
	},
	SET_PINNED_MESSAGE(state, message) {
		state.pinnedMessage = message
	},
	SET_DRAWER_PLACEMENT(state, placement) {
		state.drawerPlacement = placement
	}
}

const actions = {
	openAgent({ commit }) {
		commit('SET_OPEN', true)
		commit('SET_MINIMIZED', false)
	},
	closeAgent({ commit }) {
		commit('SET_OPEN', false)
		commit('SET_MINIMIZED', false)
		// Clear persisted state when explicitly closing
		clearPersistedState()
	},
	toggleAgent({ commit, state }) {
		const willOpen = !state.isOpen
		commit('SET_OPEN', willOpen)
		if (!willOpen) {
			commit('SET_MINIMIZED', false)
			// Clear persisted state when closing via toggle
			clearPersistedState()
		}
	},
	minimizeAgent({ commit }) {
		commit('SET_MINIMIZED', true)
	},
	maximizeAgent({ commit }) {
		commit('SET_MINIMIZED', false)
	},
	toggleMinimized({ commit, state }) {
		commit('SET_MINIMIZED', !state.isMinimized)
	},
	addUserMessage({ commit }, content) {
		commit('ADD_MESSAGE', {
			type: 'user',
			content
		})
	},
	addAgentMessage({ commit }, { content, data, formatted, fieldData, suggestions, metadata }) {
		commit('ADD_MESSAGE', {
			type: 'agent',
			content,
			data,
			formatted,
			fieldData,
			suggestions,
			metadata
		})
	},
	addSystemMessage({ commit }, content) {
		commit('ADD_MESSAGE', {
			type: 'system',
			content
		})
	},
	clearMessages({ commit }) {
		commit('CLEAR_MESSAGES')
	},
	setSelectedMode({ commit }, mode) {
		commit('SET_SELECTED_MODE', mode)
	},
	setProcessing({ commit }, isProcessing) {
		commit('SET_PROCESSING', isProcessing)
	},
	setPosition({ commit }, position) {
		commit('SET_POSITION', position)
	},
	setWidth({ commit }, width) {
		commit('SET_WIDTH', width)
	},
	setHeight({ commit }, height) {
		commit('SET_HEIGHT', height)
	},
	updateLastMessage({ commit }, updates) {
		commit('UPDATE_LAST_MESSAGE', updates)
	},
	openDrawer({ commit }, message) {
		commit('SET_PINNED_MESSAGE', message)
		commit('SET_DRAWER_OPEN', true)
		commit('SET_DRAWER_MINIMIZED', false)
	},
	closeDrawer({ commit }) {
		commit('SET_DRAWER_OPEN', false)
		commit('SET_DRAWER_MINIMIZED', false)
	},
	toggleDrawer({ commit, state }) {
		commit('SET_DRAWER_OPEN', !state.drawerOpen)
	},
	minimizeDrawer({ commit }) {
		commit('SET_DRAWER_MINIMIZED', true)
	},
	maximizeDrawer({ commit }) {
		commit('SET_DRAWER_MINIMIZED', false)
	},
	setDrawerPlacement({ commit }, placement) {
		commit('SET_DRAWER_PLACEMENT', placement)
	}
}

const getters = {
	isAgentOpen: (state) => state.isOpen,
	isMinimized: (state) => state.isMinimized,
	selectedMode: (state) => state.selectedMode,
	messages: (state) => state.messages,
	isProcessing: (state) => state.isProcessing,
	position: (state) => state.position,
	width: (state) => state.width,
	height: (state) => state.height,
	hasMessages: (state) => state.messages.length > 0,
	lastMessage: (state) => state.messages[state.messages.length - 1] || null,
	drawerOpen: (state) => state.drawerOpen,
	drawerMinimized: (state) => state.drawerMinimized,
	pinnedMessage: (state) => state.pinnedMessage,
	drawerPlacement: (state) => state.drawerPlacement
}

export default {
	namespaced: true,
	state,
	mutations,
	actions,
	getters
}
